import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ExternalLink, Package, Truck, CheckCircle2, Clock3,
  XCircle, Loader2, Store, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { API, EUR } from "@/lib/api";

const STATUS = {
  queued: { label: "À commander", cls: "bg-terra-soft text-terra", icon: Clock3 },
  ordered: { label: "Commandée chez le fournisseur", cls: "bg-sage-soft text-sage", icon: CheckCircle2 },
  shipped: { label: "Expédiée", cls: "bg-sand/70 text-ink/70", icon: Truck },
  cancelled: { label: "Annulée", cls: "bg-sand/70 text-ink/40", icon: XCircle },
};

export default function Fulfillment() {
  const [config, setConfig] = useState(null);
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);
  const [trackings, setTrackings] = useState({});

  const load = async () => {
    try {
      const [c, o] = await Promise.all([
        fetch(`${API}/supplier/config`).then((r) => r.json()),
        fetch(`${API}/supplier/orders`).then((r) => r.json()),
      ]);
      setConfig(c);
      setOrders(o.orders || []);
    } catch (e) {
      toast.error("Impossible de charger les commandes fournisseur.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (sessionId, status, extra = {}) => {
    setBusy(sessionId + status);
    try {
      await fetch(`${API}/supplier/orders/${sessionId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, ...extra }),
      });
      toast.success(
        status === "ordered"
          ? "Marquée comme commandée chez le fournisseur."
          : "Marquée comme expédiée."
      );
      await load();
    } catch (e) {
      toast.error("Mise à jour impossible. Réessayez.");
    }
    setBusy(null);
  };

  return (
    <div className="min-h-screen bg-cream text-ink font-sans" data-testid="fulfillment-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <Link to="/" data-testid="fulfillment-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-sage hover:text-sage-hover transition-colors">
          <ArrowLeft size={16} /> Retour à la boutique
        </Link>
        <h1 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight">Commandes Fournisseur</h1>
        <p className="mt-2 text-sm text-[#4A5550]">
          Chaque commande payée sur la boutique crée automatiquement une commande fournisseur ici.
        </p>

        {config && (
          <div data-testid="supplier-config-card" className="mt-8 rounded-3xl bg-white border border-sage/15 p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-sage-soft text-sage px-3 py-1 text-xs font-mono uppercase tracking-[0.15em]">
                  <Store size={13} /> {config.platform}
                </span>
                <h2 className="mt-3 font-serif text-2xl">{config.name}</h2>
                <p className="mt-1 text-sm text-[#4A5550] max-w-lg">{config.product_title}</p>
                <p className="mt-1 text-xs font-mono text-sage-mid">
                  SKU {config.sku} · {config.color} · {config.size} · ≈ {config.unit_cost_usd_range} USD / unité
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <a
                  data-testid="supplier-product-link"
                  href={config.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-sage hover:bg-sage-hover text-cream text-sm font-semibold px-5 py-3 transition-all hover:scale-[1.02]"
                >
                  <ExternalLink size={16} /> Ouvrir la fiche produit
                </a>
                <a
                  data-testid="supplier-dashboard-link"
                  href="https://cjdropshipping.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-sage/30 text-sage hover:bg-sage-soft text-sm font-semibold px-5 py-3 transition-colors"
                >
                  <Store size={16} /> Tableau de bord CJ
                </a>
              </div>
            </div>
            {config.backup_supplier && (
              <p className="mt-5 pt-5 border-t border-sage/10 text-xs text-ink/60">
                Fournisseur de secours : {config.backup_supplier.name} (
                <a data-testid="backup-supplier-link" href={config.backup_supplier.product_url} target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-2">
                  article {config.backup_supplier.ali_product_id}
                </a>
                , ≈ {config.backup_supplier.unit_cost_usd_range} USD)
              </p>
            )}
          </div>
        )}

        <div className="mt-10 flex flex-col gap-5" data-testid="supplier-orders-list">
          {orders === null && (
            <p className="text-sm text-ink/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
          )}
          {orders !== null && orders.length === 0 && (
            <div data-testid="supplier-empty-state" className="rounded-3xl border border-dashed border-sage/25 p-10 text-center">
              <Package size={28} className="mx-auto text-sage/40" />
              <p className="mt-4 font-medium text-ink/70">Aucune commande pour le moment.</p>
              <p className="mt-1 text-sm text-[#4A5550]">
                Dès qu'un client paie sur la boutique, sa commande apparaît ici automatiquement
                avec son adresse de livraison et le lien de commande fournisseur.
              </p>
            </div>
          )}
          {orders && orders.map((o, i) => {
            const st = STATUS[o.status] || STATUS.queued;
            return (
              <motion.div
                key={o.session_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`supplier-order-card-${i}`}
                className="rounded-3xl bg-white border border-sage/15 p-6 sm:p-7"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{o.customer_name || "Client"}</p>
                    <p className="text-xs font-mono text-ink/50 mt-1">Session {o.session_id.slice(0, 18)}…</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${st.cls}`} data-testid={`supplier-order-status-${i}`}>
                    <st.icon size={13} /> {st.label}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="rounded-2xl bg-cream/70 border border-sage/10 p-4">
                    <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-mid">Produit</p>
                    <p className="mt-1.5">{o.pack_label} — {o.units} coussin{o.units > 1 ? "s" : ""}</p>
                    <p className="mt-1 text-ink/60">Vendu {EUR(o.amount)} · coût fournisseur ≈ {o.units * 20} USD</p>
                  </div>
                  <div className="rounded-2xl bg-cream/70 border border-sage/10 p-4">
                    <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-mid">Livraison</p>
                    <p className="mt-1.5">{o.shipping_address || "—"}</p>
                    {o.customer_phone && <p className="mt-1 text-ink/60">{o.customer_phone}</p>}
                    {o.customer_email && <p className="text-ink/60">{o.customer_email}</p>}
                  </div>
                </div>
                {o.supplier_order_id && (
                  <p className="mt-3 text-xs font-mono text-ink/60">N° commande fournisseur : {o.supplier_order_id}</p>
                )}
                {o.tracking_number && (
                  <p className="mt-1 text-xs font-mono text-ink/60">Suivi : {o.tracking_number}</p>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  {o.status === "queued" && (
                    <>
                      <a
                        data-testid={`supplier-order-open-${i}`}
                        href={o.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-sage hover:bg-sage-hover text-cream text-sm font-semibold px-5 py-2.5 transition-all hover:scale-[1.02]"
                      >
                        <ExternalLink size={15} /> Commander chez le fournisseur
                      </a>
                      <button
                        data-testid={`supplier-order-mark-ordered-${i}`}
                        onClick={() => setStatus(o.session_id, "ordered")}
                        disabled={busy === o.session_id + "ordered"}
                        className="inline-flex items-center gap-2 rounded-full border border-sage/30 px-5 py-2.5 text-sm font-semibold text-sage hover:bg-sage-soft transition-colors disabled:opacity-50"
                      >
                        <CheckCircle2 size={15} /> Marquer commandée
                      </button>
                    </>
                  )}
                  {o.status === "ordered" && (
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                      <input
                        data-testid={`supplier-tracking-input-${i}`}
                        value={trackings[o.session_id] || ""}
                        onChange={(e) => setTrackings({ ...trackings, [o.session_id]: e.target.value })}
                        placeholder="N° de suivi du colis"
                        className="flex-1 min-w-[200px] rounded-full border border-sage/20 bg-white px-5 py-2.5 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                      />
                      <button
                        data-testid={`supplier-order-mark-shipped-${i}`}
                        onClick={() => setStatus(o.session_id, "shipped", { tracking_number: trackings[o.session_id] || "" })}
                        disabled={busy === o.session_id + "shipped"}
                        className="inline-flex items-center gap-2 rounded-full bg-sage hover:bg-sage-hover text-cream text-sm font-semibold px-5 py-2.5 transition-all hover:scale-[1.02] disabled:opacity-50"
                      >
                        <Truck size={15} /> Marquer expédiée
                      </button>
                    </div>
                  )}
                  {o.status === "shipped" && (
                    <button
                      data-testid={`supplier-order-copy-tracking-${i}`}
                      onClick={() => { navigator.clipboard?.writeText(o.tracking_number || ""); toast.success("N° de suivi copié."); }}
                      className="inline-flex items-center gap-2 text-sm text-sage hover:text-sage-hover transition-colors"
                    >
                      <Copy size={15} /> Copier le n° de suivi
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
